



















































// Libraries
import { Component, Vue, Watch } from 'vue-property-decorator'
import _ from 'lodash'
import moment from 'moment'

import { db } from '@/database'

// Types
import { MatchConfigViewModel } from '@/types/view-models/match-config'

import matchConfigPresets from 'match-central-shared/dist/match-config-presets.js'

// Views
import MainLayout from '@/views/layout/Main.vue'
import MatchForm from '@/components/Forms/MatchForm.vue'
import MatchConfigCustomisationForm from '@/components/Forms/MatchConfigCustomisationForm.vue'
import { convertConfigToViewModel, convertViewModelToConfig } from '@/utility/match-config'

const DEFAULT_SPORT = '-- Select sport --'
const DEFAULT_CONFIG_PRESET = '-- Select configuration preset --'
const CUSTOM_CONFIG_PRESET_NAME = 'Customize'

const SPORT_OPTIONS: readonly string[] = matchConfigPresets.reduce((sports: string[], preset) => {
  preset.relatedSports.forEach((sport) => sports.includes(sport) || sports.push(sport))
  return sports
}, [])

const MATCH_CONFIG_TEMPLATE: MatchConfigViewModel = {
  regularPeriods: {
    quantity: 1,
    duration: '20:00',
    allowExtraTime: false,
  },
  overtimePeriods: null,
  penalties: null,
}

const DEFAULT_FORM = Object.freeze({
  kickoff: {
    date: new Date().toISOString().substr(0, 10),
    time: '',
  },

  hName: '',
  hAbbr: '',

  vName: '',
  vAbbr: '',

  sport: DEFAULT_SPORT,
  config: null,
})

@Component({
  components: {
    MainLayout,
    MatchForm,
    MatchConfigCustomisationForm,
  },
})
export default class NewMatch extends Vue {
  loading = false

  form = JSON.parse(JSON.stringify(DEFAULT_FORM))

  errors = {}

  configurationPreset = DEFAULT_CONFIG_PRESET

  latestPresetName = ''
  latestOvertimeSettings = null
  latestPenaltiesSettings = null

  CUSTOM_CONFIG_PRESET_NAME = CUSTOM_CONFIG_PRESET_NAME

  @Watch('configurationPreset')
  onConfigurationPresetChanged(val: string, oldval: string): void {
    // Chose default value
    if (val === DEFAULT_CONFIG_PRESET) {
      return
    }

    // Check if wanting to create custom preset
    if (CUSTOM_CONFIG_PRESET_NAME === val) {
      // From existing preset
      if (oldval && matchConfigPresets.find((p) => p.title === oldval)) {
        this.latestPresetName = oldval
      } else {
        // Overwrite config settings with config template
        this.$set(this.form, 'config', MATCH_CONFIG_TEMPLATE)
        // console.log('Match config applied from template')
      }
    }

    // Get chosen preset with this title
    const chosenPreset = matchConfigPresets.find((p) => p.title === val)
    if (chosenPreset) {
      // If preset found then overwrite form config with presets config
      // console.log(
      //   'Overwrite match config settings with preset',
      //   chosenPreset.title,
      //   chosenPreset.config
      // )
      // Overwrite current config settings
      this.$set(this.form, 'config', convertConfigToViewModel(chosenPreset.config))
      // console.log('New match config settings after overwrite', this.form.config)

      return
    }
  }

  get sportsOptions(): readonly string[] {
    return [
      // Default sport
      DEFAULT_SPORT,
      // Sports extracted from existing presets
      ...SPORT_OPTIONS,
    ]
  }

  get matchConfigPresetsOptions(): readonly string[] {
    return [
      // Default value (select...)
      DEFAULT_CONFIG_PRESET,
      // Custom config preset
      CUSTOM_CONFIG_PRESET_NAME,
      // Titles extracted from existing presets
      ...matchConfigPresets.map((p) => p.title),
    ]
  }

  /**
   * Get errors
   */
  getErrors(): { [key: string]: string[] } {
    const f = this.form

    const errors: { [key: string]: string[] } = {}

    // Validate team names
    if (f.hName.length < 2) {
      'hName' in errors || (errors.hName = [])
      errors.hName.push('Missing team name')
    }
    if (f.vName.length < 2) {
      'vName' in errors || (errors.vName = [])
      errors.vName.push('Missing team name')
    }

    // Validate match configuration
    if (!f.config) {
      'config' in errors || (errors['config'] = [])
      errors['config'].push('Missing match config')
    } else {
      // Number of Regular periods must be 1 or more
      if (f.config.regularPeriods.quantity < 1) {
        'config.regularPeriods.quantity' in errors ||
          (errors['config.regularPeriods.quantity'] = [])
        errors['config.regularPeriods.quantity'].push('Missing number of regular periods')
      }
    }

    return errors
  }

  /**
   * Validate
   */
  validates(saveErrors: boolean): boolean {
    const f = this.form

    const errors = this.getErrors()

    if (saveErrors) {
      this.errors = errors
    }
    if (Object.values(errors).length > 0) {
      return false
    }

    // Team abbreviations
    // if (f.hAbbr.length < 2) {
    //   return false
    // }
    // if (f.vAbbr.length < 2) {
    //   return false
    // }

    // Guard no config set
    if (!f.config) {
      return false
    }

    return true
  }

  get validatesWithoutSavingErrors(): boolean {
    return this.validates(false)
  }

  get canReset(): boolean {
    return !_.isEqual(this.form, DEFAULT_FORM)
  }

  /**
   * Toggle overtime periods
   */
  toggleOvertimePeriods(): void {
    // Toggle from currently enabled to disabled
    if (this.form.config.overtimePeriods) {
      // Save as latest overtime settings
      this.latestOvertimeSettings = this.form.config.overtimePeriods

      this.$set(this.form.config, 'overtimePeriods', false)
      return
    }

    // Enable overtime periods config
    this.$set(
      this.form.config,
      'overtimePeriods',
      // Recall latest overtime settings or default
      this.latestOvertimeSettings || {
        quantity: 1,
        duration: '10:00',
        allowExtraTime: false,
      }
    )
  }

  /**
   * Toggle penalties enabled in form
   */
  togglePenalties(): void {
    // Toggle from currently enabled to disabled
    if (this.form.config.penalties) {
      // Save as latest penalties settings
      this.latestPenaltiesSettings = this.form.config.penalties

      this.$set(this.form.config, 'penalties', false)
      return
    }

    // Enable penalties config
    this.$set(
      this.form.config,
      'penalties',
      // Recall latest penalties settings or default
      this.latestPenaltiesSettings || {
        durations: [],
        requiresPlayerJerseyNumber: false,
      }
    )
  }

  save(): void {
    // console.log('Initiating save match')

    if (!this.validates(true)) {
      window.flash('Match data not validated!', 'error')
      return
    }

    this.loading = true
    this.errors = {}

    // Parse match object from form
    const match = JSON.parse(JSON.stringify(this.form))

    // Parse kickoff to Javascript Date object
    match.kickoff = moment(match.kickoff.date + ' ' + match.kickoff.time).toDate()

    // If empty/default sport set - replace with empty
    if (match.sport === DEFAULT_SPORT) {
      match.sport = ''
    }

    // Guard no config set at all...
    if (!match.config) {
      // console.error('No match config....', match.config)
      return
    }

    // Reset overtime period config if quantity=0
    if (match.config.overtimePeriods && match.config.overtimePeriods.quantity === 0) {
      match.config.overtimePeriods = null
    }

    // Convert config vm to model obj
    match.config = convertViewModelToConfig(match.config)

    // console.log('Attempting to save match', match)

    db.collection('matches')
      .add(match)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((_response: unknown) => {
        window.flash('Match saved!')

        // console.log('Saved match! Firestore response:', _response)

        this.resetForm()
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((_err: unknown) => {
        window.flash('Unable to save match... Try again...', 'error')

        // console.error('Error saving match! Firestore err:', _err)
      })
      .finally(() => {
        this.resetForm()
      })
  }

  resetForm(): void {
    this.form = JSON.parse(JSON.stringify(DEFAULT_FORM))
    this.configurationPreset = DEFAULT_CONFIG_PRESET
    this.errors = {}
    this.loading = false
  }

  comingSoon(): void {
    window.flash('This feature is coming soon!', 'warning')
  }
}
